import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, X } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmt = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const AudioBar = () => {
  const { currentTrack, isPlaying, setIsPlaying, playTrack, close, audioRef } = usePlayer();
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    if (isPlaying) audio.play().catch(() => setIsPlaying(false));
    else audio.pause();
  }, [isPlaying, currentTrack, audioRef, setIsPlaying]);

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    if (audioRef.current && duration) audioRef.current.currentTime = ratio * duration;
  };

  return (
    <AnimatePresence>
      {currentTrack && (
        <motion.div
          key={currentTrack.id}
          initial={{ y: 90 }}
          animate={{ y: 0 }}
          exit={{ y: 90 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          data-testid="audio-bar"
          className="fixed bottom-0 inset-x-0 z-50 bg-sand-50/95 backdrop-blur-md border-t border-sand-200"
        >
          <audio
            ref={audioRef}
            src={`${BACKEND}/api/files/${currentTrack.storage_path}`}
            onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
            onEnded={() => setIsPlaying(false)}
          />
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-5">
            <button
              type="button"
              onClick={() => playTrack(currentTrack)}
              data-testid="audio-bar-toggle"
              className="w-11 h-11 shrink-0 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-sand-50 flex items-center justify-center transition-colors duration-300"
            >
              {isPlaying ? <Pause size={17} /> : <Play size={17} className="ml-0.5" />}
            </button>
            <div className="min-w-0 w-40 shrink-0 hidden sm:block">
              <p className="font-serif text-lg font-light text-sand-900 truncate">{currentTrack.title}</p>
              <p className="font-sans text-[11px] font-light tracking-[0.2em] uppercase text-sand-900/40">Frank en Vrij</p>
            </div>
            <span className="font-sans text-xs font-light text-sand-900/50 w-10 text-right shrink-0">{fmt(time)}</span>
            <div
              onClick={seek}
              data-testid="audio-bar-progress"
              className="flex-1 h-6 flex items-center cursor-pointer group"
            >
              <div className="w-full h-1 bg-sand-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-terracotta-500 transition-[width] duration-150"
                  style={{ width: duration ? `${(time / duration) * 100}%` : "0%" }}
                />
              </div>
            </div>
            <span className="font-sans text-xs font-light text-sand-900/50 w-10 shrink-0">{fmt(duration)}</span>
            <button
              type="button"
              onClick={close}
              data-testid="audio-bar-close"
              className="shrink-0 text-sand-900/40 hover:text-terracotta-500 transition-colors duration-300"
            >
              <X size={18} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
